import React from 'react';
import { Building2, PenTool, Home, TrendingUp } from 'lucide-react';

const WhoItsFor = () => {
  const audiences = [
    {
      icon: Home,
      title: 'Homeowners & Homebuyers',
      description:
        'Take control of your renovation or new home project. Visualize every detail, avoid costly mistakes, and order everything directly without dealing with contractors.',
      benefits: [
        'See before you build',
        'Avoid design mistakes',
        'Direct ordering platform',
        'Transparent pricing',
      ],
      image: 'https://images.pexels.com/photos/1109541/pexels-photo-1109541.jpeg',
    },
    {
      icon: PenTool,
      title: 'Interior Designers',
      description:
        'Present your designs in stunning AR. Collaborate with clients in real-time and close projects faster with instant client approvals.',
      benefits: [
        'Instant client approvals',
        'AR presentations',
        'Real-time collaboration',
        'Professional portfolio',
      ],
      image: 'https://images.pexels.com/photos/32913099/pexels-photo-32913099.jpeg',
    },
    {
      icon: Building2,
      title: 'Property Developers',
      description:
        'Let buyers experience properties before construction. Increase pre-sales and reduce uncertainty with immersive AR walkthroughs.',
      benefits: [
        'Pre-sell faster',
        'Reduce change orders',
        'Buyer confidence',
        'Marketing advantage',
      ],
      image: 'https://images.pexels.com/photos/34840277/pexels-photo-34840277.jpeg',
    },
    {
      icon: TrendingUp,
      title: 'Furniture & Material Retailers',
      description:
        'Connect directly with homeowners planning projects. Showcase your products in customer spaces through AR.',
      benefits: [
        'Direct customer access',
        'AR product showcase',
        'Increased conversions',
        'Verified sales channel',
      ],
      image: 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
    },
  ];

  return (
    <section className="relative py-32 px-6 md:px-[7.6923%]">
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%), repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)',
          backgroundSize: '100% 100%',
          opacity: 0.03,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-20">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-6"
            style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Built for Homeowners First
          </h2>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            Our platform empowers homeowners to create their dream spaces with confidence, transparency, and complete control.
          </p>
        </div>

        {/* Audiences */}
        <div className="space-y-20">
          {audiences.map((audience, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              } gap-8 md:gap-12 items-center`}
            >
              {/* Content */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 flex items-center justify-center rounded-2xl"
                    style={{
                      borderColor: 'var(--brand-primary)',
                      background: 'linear-gradient(135deg, rgba(255,128,0,0.15) 0%, rgba(255,215,0,0.15) 100%)',
                    }}
                  >
                    <audience.icon size={32} color="#FF8000" strokeWidth={1.5} />
                  </div>
                  <h3
                    className="text-3xl md:text-4xl font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {audience.title}
                  </h3>
                </div>

                <p
                  className="text-lg md:text-xl"
                  style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
                >
                  {audience.description}
                </p>

                {/* Benefits */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {audience.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 flex-shrink-0"
                        style={{ backgroundColor: 'var(--brand-primary)' }}
                      />
                      <span className="text-base" style={{ color: 'var(--text-secondary)' }}>
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className="flex-1 relative group">
                <div className="relative overflow-hidden">
                  <img
                    src={audience.image}
                    alt={audience.title}
                    className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 border-2 transition-opacity duration-400 opacity-0 group-hover:opacity-100"
                    style={{ borderColor: 'var(--brand-primary)' }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <a href="#register">
            <button className="bg-[#FF8000] text-black border-none rounded-2xl px-8 py-4 text-lg font-medium cursor-pointer transition-all duration-400 hover:bg-[rgba(0,255,209,0.1)] hover:text-[#FF8000] min-h-[56px] flex items-center justify-center gap-3 mx-auto">
              Register Your Interest
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhoItsFor;